import { signupIdentityConfig, type SignupCredentials } from '@/utils/signupIdentity';
import type { HomeFeed } from '@/pages/HomeV2/homeFeed';
import type { FaqGroupCode, FaqListResponse } from '@/api/repository/types/faq';
import type { PrizeItem, PrizeSubmitPayload } from '@/api/repository/types/prize';
// API response type
import { unregisterPushDevice } from '@/native/capacitorApp';
import type { AppleLoginRequest } from '@/native/appleSignIn';
import axios, { AxiosResponse } from 'axios';
import { prepareImageForUpload } from '@/utils/compressImage';
import { getCookie } from '@/common/cookie';
import { navigationService } from '@/common/navigationService';
import { clearListSnapshots, syncFavoriteListSnapshots } from '@/common/listRestore';
import {
    ClinicsResponse,
    ClinicsSignUpRequest,
    CommentRequest,
    HomeRecruitmentList,
    ModifyClinicInfoRequest,
    ClinicReadiness,
    HomepagePreview,
    BusinessCertUploadResult,
    SignupHospitalCheckResult,
    ClinicEmailChangeRequestPayload,
    ClinicEmailChangeRequestResult,
    SignupHomepageCheckResult,
    PostRequest,
    SokdakAlias,
    RecruimentCreationRequest,
    ReplyRequest,
    ResumeMetaType,
    ResumeRegisterType,
    ResumeViewResponse,
    ApplicantResumeViewResponse,
    SignUpRequest,
    SubwayArea,
    SubwayStation,
    SubwayStationResponse,
    UpdateOptionalAgreementsPayload,
    UserAgreementResponse,
    PushAgreementsResponse,
    SavedFilterResponse,
    SavedFiltersResponse,
    NotificationPage,
    NotificationItem,
    ValidateResponse,
    LicenseVerificationResponse,
    LicenseVerificationSubmitPayload,
    LicenseVerificationSubmitResult,
    SalaryBaselineComparison,
    SalaryBaselineUnavailable,
    SalarySurveyCurrentResponse,
    RecruitmentSurveyPrompt,
    RecruitmentSurveyPayload,
    RecruitmentSurveySubmitResult,
    SalarySurveyMyResult,
    SalarySurveyMyResultUnavailable,
    SalarySurveyReport,
    SalarySurveyResponsePayload,
    SalarySurveySaveResult,
    InstructorSettlement,
    InstructorSettlementOverview,
    SeminarClaimableCoupon,
    ApplicableCouponsResponse,
    MyCoupon,
    MyCouponStatus,
    ClaimByCodeResult,
    SeminarPaymentPrepareResult,
    SeminarOnlineAccessOpenResult,
    InstructorCoupon,
    InstructorCouponPayload,
    RefundQuoteCoupon,
    SimilarRecruitmentQuery,
} from './types/types';
import {
    AiChatAccess,
    AiChatDetail,
    AiChatFeedbackPayload,
    AiChatListResult,
    AiChatStatus,
    AiChatSuggestions,
    AiChatHooks,
    AiRecruitmentFlowPublished,
    AiRecruitmentFlowFillResult,
    AiRepostResult,
    AiRecruitmentFlowView,
    AiLoungeDraft,
    AiRecruitmentDraft,
    AiRecruitmentDraftCreated,
    AiCommunityDraft,
    AiShareCheckResult,
    AiShareLink,
    AiSharePayload,
    AiShareReportReason,
    AiShareView,
} from './types/aiChat';
import { URLS } from './urls/urls';
import {City, District} from '@/pages/Home/types';
import {Recruitment, RecruitmentList, TodayRecruitmentList} from "@/components/domain/recruitment/types";

// 요청별로 401 인터셉터의 토스트/리다이렉트를 생략할 수 있도록 config 타입 확장
declare module 'axios' {
    interface AxiosRequestConfig {
        skipAuthHandler?: boolean;
    }
}

/**
 * 목록 내용이 달라지는 요청인지 — 성공하면 뒤로가기 복원용 목록 저장본(common/listRestore)을 전부 버린다.
 * 글·라운지 콘텐츠·세미나 삭제/폐강, 사용자 차단/해제가 해당한다. 관심 변경은 표시만 갱신한다.
 * (조회수 기록·이벤트 전송처럼 상세를 열 때마다 나가는 POST 는 포함하면 안 된다 — 복원 자체가 무의미해진다)
 */
const DELETE_AFFECTS_LISTS: readonly RegExp[] = [
    /^\/api\/posts\/[^/]+$/,
    /^\/api\/expert-contents\/[^/]+$/,
    /^\/api\/seminars\/[^/]+(\/cancel)?$/,
];
const ANY_MUTATION_AFFECTS_LISTS: readonly RegExp[] = [/^\/api\/users\/[^/]+\/block$/, /^\/api\/seminars\/[^/]+\/cancel$/];

function affectsLists(method: string | undefined, url: string | undefined): boolean {
    if (!method || !url) return false;
    const verb: string = method.toLowerCase();
    if (verb === 'get' || verb === 'head' || verb === 'options') return false;
    const path: string = url.split('?')[0];
    if (ANY_MUTATION_AFFECTS_LISTS.some((pattern: RegExp) => pattern.test(path))) return true;
    return verb === 'delete' && DELETE_AFFECTS_LISTS.some((pattern: RegExp) => pattern.test(path));
}

// 쿠키를 자동으로 포함하도록 설정
axios.defaults.withCredentials = true;

// Request interceptor - 요청마다 토큰 추가
axios.interceptors.request.use(
    (config) => {
        const accessToken = getCookie('MAC');
        if (accessToken && accessToken.length > 0) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Response interceptor - 401 에러 처리
axios.interceptors.response.use(
    (response) => {
        // 방어: CloudFront 등이 /api 응답을 SPA index.html(text/html)로 위장해 200으로 내려주는 경우,
        // JSON 대신 HTML 문자열이 성공 응답으로 들어온다. 이를 명확한 에러로 전환해 "뭉뚱그린 실패"를 막는다.
        const contentType = String(response.headers?.['content-type'] || '');
        const looksLikeHtml =
            contentType.includes('text/html') ||
            (typeof response.data === 'string' && /^\s*<(!doctype|html)/i.test(response.data));
        if (looksLikeHtml) {
            return Promise.reject({
                config: response.config,
                response: {
                    status: 403,
                    data: {
                        code: 403,
                        message: '요청이 정상 처리되지 않았습니다. 로그인이 만료되었거나 권한이 없을 수 있어요. 다시 로그인한 뒤 시도해 주세요.',
                    },
                },
            });
        }
        if (affectsLists(response.config?.method, response.config?.url)) {
            clearListSnapshots();
        }
        const clinicFavorite = response.config?.url?.split('?')[0].match(/^\/api\/favorites\/clinics\/(\d+)$/);
        const method = response.config?.method?.toLowerCase();
        if (clinicFavorite && (method === 'post' || method === 'delete') &&
            (response.data?.code === 0 || response.data?.code === 200) && typeof response.data?.result?.isFavorite === 'boolean') {
            syncFavoriteListSnapshots({ kind: 'clinic', clinicId: Number(clinicFavorite[1]), isFavorite: response.data.result.isFavorite });
        }
        // 정상 응답은 그대로 반환
        return response;
    },
    (error) => {
        // 401 Unauthorized 에러 처리 (JWT 토큰 만료 등)
        if (error.response && error.response.status === 401) {
            const path = window.location.pathname;
            // skipAuthHandler가 설정된 요청(로그인 상태 조용히 확인 등)은 토스트/리다이렉트 생략
            const skipAuthHandler = error.config?.skipAuthHandler === true;
            // 로그인/콜백/회원가입 페이지에서는 리다이렉트 안 함
            if (!skipAuthHandler && !path.startsWith('/login') && !path.startsWith('/signup')) {
                navigationService.showToast('로그인이 필요합니다.', 'error');
                // 로그인 완료 후 원래 보던 페이지로 복귀
                navigationService.navigateTo(`/login?returnUrl=${encodeURIComponent(path + window.location.search)}`);
            }
        }

        // 403 Forbidden: 백엔드 Security 기본 응답은 message가 없어 화면이 뭉뚱그려진다. 사유를 채워준다.
        if (error.response && error.response.status === 403) {
            const data = error.response.data;
            const hasMessage = data && typeof data === 'object' && typeof data.message === 'string' && data.message.length > 0;
            if (!hasMessage) {
                error.response.data = {
                    ...(typeof data === 'object' ? data : {}),
                    code: 403,
                    message: '권한이 없습니다. 강사 승인 상태 또는 로그인 상태를 확인한 뒤 다시 시도해 주세요.',
                };
            }
        }

        return Promise.reject(error);
    },
);

/** 급여 실태조사 회차 키 자리에 쓰는 별칭 — 지금 응답을 받는 회차 */
const CURRENT_CAMPAIGN_KEY = 'current';

/** 급여 실태조사 회차 키·기준선 연도 자리에 쓰는 별칭 — 가장 최근 발행분(기준선은 최신 연도) */
const LATEST_KEY = 'latest';

export const api = {
    idValidate: async (userId: string) => {
        const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.AUTH.ID_VALIDATE(userId),
        );
        return data;
    },
    nickNameValidate: async (nickName: string) => {
        const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.AUTH.NICK_NAME_VALIDATE(nickName),
        );
        return data;
    },
    emailValidate: async (email: string) => {
        const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.AUTH.EMAIL_VALIDATE(email),
        );
        return data;
    },
    getSubwayStations: async (lineId: string) => {
        const { data } = await axios.get<any, AxiosResponse<SubwayStationResponse>>(
            URLS.SEARCH.SUBWAY_STATIONS(lineId),
        );
        return data;
    },
    signUp: async (payload: SignUpRequest) => {
        const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.AUTH.SIGNUP,
            payload,
        );
        return data;
    },
    clinicsSignUp: async (payload: ClinicsSignUpRequest) => {
        const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.AUTH.CLINICS_SIGNUP,
            payload,
        );
        return data;
    },

    recruitments: {
        meta: async (id: number) => {
            const { data } = await axios.get(`/api/recruitments/meta`, { params: { clinicId: id } });
            return data;
        },
        detail: async (id: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Recruitment>>>(`/api/recruitments/${id}`);
            return data;
        },
        /** 상세 화면 미리보기 — 저장 전 등록 요청 값을 상세 응답 모양으로(저장하지 않는다, 본인 치과만) */
        preview: async (payload: unknown) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<Recruitment>>>('/api/recruitments/preview', payload);
            return data;
        },
        /** 비슷한 공고 — 채용 목록 주소 쿼리(`category=REGION&districts=…`)와 그 조건의 건수. 0건이면 서버가 조건을 넓혀 고른다 */
        similarQuery: async (recruitmentId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SimilarRecruitmentQuery>>>(
                URLS.RECRUITMENTS.SIMILAR_QUERY(recruitmentId),
            );
            return data;
        },
        // 조회수 증가 (쿠키 기반 중복 방지 — 6시간). 상세 진입 시 detail과 별도로 1회 호출.
        incrementView: async (id: number) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(`/api/recruitments/${id}/view`);
            return data;
        },
        applications: async (recruitmentId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RECRUITMENTS.APPLICATIONS(recruitmentId)
            );
            return data;
        },
        // 여러 공고의 전체/미열람 지원자 수 배치 조회 — 공고별 applications 개별 호출(N+1) 대체
        applicationSummaries: async (recruitmentIds: number[]) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                '/api/recruitments/applications/summary',
                { params: { recruitmentIds: recruitmentIds.join(',') } }
            );
            return data;
        },
        /** excludeDeleted — 본인 치과 조회에서도 삭제 공고를 뺀다(채용 상세 '다른 채용'). 채용공고 관리 [삭제] 탭은 빼지 않는다 */
        byClinic: async (clinicId: number, limit?: number, offset?: number, excludeDeleted?: boolean) => {
            const params: any = {};
            if (limit !== undefined) params.limit = limit;
            if (offset !== undefined) params.offset = offset;
            if (excludeDeleted) params.excludeDeleted = true;
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/recruitments/clinic/${clinicId}`,
                { params }
            );
            return data;
        },
        search: async (keyword: string, limit: number, offset: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<RecruitmentList>>>(
                '/api/recruitments/search',
                {
                    params: { keyword, limit, offset },
                    // Axios 기본 변환은 []를 다시 풀어 Tomcat이 요청을 거부한다. 검색 문자는 그대로 인코딩한다.
                    paramsSerializer: (params: Record<string, unknown>) => Object.entries(params)
                        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                        .join('&'),
                }
            );
            return data;
        },
        regionList: async (
            regionIds: string[],
            jobTaskTypes: string[],
            careerTypes: string[],
            workingDaysTypes: string[],
            limit: number,
            offset: number,
            recruitmentTypes: string[] = [],
            emergency?: boolean,
        ) => {
            const params: any = {
                limit,
                offset,
            };

            if (regionIds && regionIds.length > 0) {
                params.regionIds = regionIds;
            }
            if (jobTaskTypes && jobTaskTypes.length > 0) {
                params.jobTaskTypes = jobTaskTypes;
            }
            if (careerTypes && careerTypes.length > 0) {
                params.careerTypes = careerTypes;
            }
            if (workingDaysTypes && workingDaysTypes.length > 0) {
                params.workingDaysTypes = workingDaysTypes;
            }
            if (recruitmentTypes && recruitmentTypes.length > 0) {
                params.recruitmentTypes = recruitmentTypes;
            }
            if (emergency === true) {
                params.emergency = true;
            }

            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<RecruitmentList>>>(
                URLS.RECRUITMENTS.REGION(),
                {
                    params,
                    paramsSerializer: params => {
                        return Object.entries(params).map(([key, value]) => {
                            if (Array.isArray(value)) {
                                return value.map(val => `${key}=${encodeURIComponent(val)}`).join('&');
                            }
                            return `${key}=${encodeURIComponent(value)}`;
                        }).join('&');
                    }
                }
            );
            return data;
        },
        subwayList: async (
            stationIds: string[],
            jobTaskTypes: string[],
            careerTypes: string[],
            workingDaysTypes: string[],
            limit: number,
            offset: number,
            recruitmentTypes: string[] = [],
            emergency?: boolean,
            areaIds: string[] = [],
        ) => {
            const params: any = {
                limit,
                offset,
            };

            if (stationIds && stationIds.length > 0) {
                params.stationIds = stationIds;
            }
            // 지역 전체(수도권 전체 등) — 라인 나열 없이 areaId 하나로 그 지역 전체 채용 필터
            if (areaIds && areaIds.length > 0) {
                params.areaIds = areaIds;
            }
            if (jobTaskTypes && jobTaskTypes.length > 0) {
                params.jobTaskTypes = jobTaskTypes;
            }
            if (careerTypes && careerTypes.length > 0) {
                params.careerTypes = careerTypes;
            }
            if (workingDaysTypes && workingDaysTypes.length > 0) {
                params.workingDaysTypes = workingDaysTypes;
            }
            if (recruitmentTypes && recruitmentTypes.length > 0) {
                params.recruitmentTypes = recruitmentTypes;
            }
            if (emergency === true) {
                params.emergency = true;
            }

            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<RecruitmentList>>>(
                '/api/recruitments/station',
                {
                    params,
                    paramsSerializer: params => {
                        return Object.entries(params).map(([key, value]) => {
                            if (Array.isArray(value)) {
                                return value.map(val => `${key}=${encodeURIComponent(val)}`).join('&');
                            }
                            return `${key}=${encodeURIComponent(value)}`;
                        }).join('&');
                    }
                }
            );
            return data;
        },
        /**
         * 채용 목록 한 페이지(조건 객체) — 지역 목록(`/recruitments/region`) 또는 지하철 목록(`/recruitments/station`).
         * 조건 값은 배열이면 같은 이름으로 여러 번 붙인다(`jobTaskTypes=A&jobTaskTypes=B`). 페이지 목록 조립은 pages/Recruitments/recruitmentListQuery.ts
         */
        listPage: async (kind: 'region' | 'station', params: Record<string, string | string[]>, limit: number, offset: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<RecruitmentList>>>(
                kind === 'region' ? URLS.RECRUITMENTS.REGION() : '/api/recruitments/station',
                {
                    params: { ...params, limit, offset },
                    paramsSerializer: (all: Record<string, unknown>) => Object.entries(all)
                        .flatMap(([key, value]) => (Array.isArray(value) ? value : [value])
                            .map((item) => `${key}=${encodeURIComponent(String(item))}`))
                        .join('&'),
                },
            );
            return data;
        },
        /** 설문 보상 공고는 일반 목록 페이지·정렬과 별도로 현재 조건에 대해서만 조회한다. */
        today: async (scope: 'region' | 'station' | 'search', params: Record<string, string | string[]>) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<TodayRecruitmentList>>>(
                '/api/recruitments/today',
                {
                    params: { ...params, scope },
                    skipAuthHandler: true,
                    paramsSerializer: (all: Record<string, unknown>) => Object.entries(all)
                        .flatMap(([key, value]) => (Array.isArray(value) ? value : [value])
                            .map((item) => `${key}=${encodeURIComponent(String(item))}`))
                        .join('&'),
                },
            );
            return data;
        },
        changeStatus: async (recruitmentId: number, status: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/recruitments/${recruitmentId}/status`,
                { status }
            );
            return data;
        },
        /** 끌올 — 하루(KST 0시 기준) 한 번 공개 목록 맨 위로. 응답 result.bumpedAt(KST). 오늘 이미면 409 */
        bump: async (recruitmentId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ bumpedAt: string }>>>(
                `/api/recruitments/${recruitmentId}/bump`
            );
            return data;
        },
    },

    user: {
        profile: async (options?: { silent?: boolean }) => {
            // silent: 로그인 상태를 조용히 확인하는 용도. 401이어도 토스트/리다이렉트 하지 않음
            const { data } = await axios.get(URLS.USER.ME, options?.silent ? { skipAuthHandler: true } : undefined);
            return data;
        },
        // 경량 사용자 정보 — 헤더/권한 게이트/글쓰기 등 공용 화면용.
        // 주소·생년월일 등 상세 정보는 마이페이지에서만 profile()로 조회한다.
        summary: async (options?: { silent?: boolean }) => {
            const { data } = await axios.get(URLS.USER.ME_SUMMARY, options?.silent ? { skipAuthHandler: true } : undefined);
            return data;
        },
        updateUserInfo: async (payload: any) => {
            const { data } = await axios.put(URLS.USER.UPDATE_PROFILE, payload);
            return data;
        },
        // 내 약관 동의 상태 조회 (필수 4 + 선택 + termsVersion / updateAt)
        getAgreements: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<UserAgreementResponse>>>(
                URLS.USER.AGREEMENTS,
            );
            return data;
        },
        // 선택 약관 부분 변경 (resumePublic / recommendation / briefEmail / briefKakao).
        // 누락된 키는 변경하지 않음. 필수 약관은 변경 불가.
        updateAgreements: async (payload: UpdateOptionalAgreementsPayload) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<UserAgreementResponse>>>(
                URLS.USER.AGREEMENTS,
                payload,
            );
            return data;
        },
        // 푸시 설정 조회 (광고성/서비스 그룹별 토픽 수신 상태)
        getPushAgreements: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<PushAgreementsResponse>>>(
                URLS.USER.PUSH_AGREEMENTS,
            );
            return data;
        },
        // 푸시 설정 부분 변경. { [토픽]: 수신여부 } — 전달된 토픽만 변경.
        updatePushAgreements: async (agreements: Record<string, boolean>) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<PushAgreementsResponse>>>(
                URLS.USER.PUSH_AGREEMENTS,
                { agreements },
            );
            return data;
        },
        // 목록(채용·세미나)에서 저장한 조건. silent: 목록 진입 때 조용히 읽는다(401 이어도 토스트·로그인 이동 없음)
        getSavedFilters: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SavedFiltersResponse>>>(
                URLS.USER.SAVED_FILTERS,
                { skipAuthHandler: true },
            );
            return data;
        },
        // 조건 저장(덮어쓰기). query: 목록 주소 쿼리, params: 같은 조건의 목록 API 인자, notify: 조건에 맞는 새 글 알림
        saveFilter: async (domain: 'recruitment' | 'seminar', payload: { query: string; params: Record<string, string[]>; notify: boolean }) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<SavedFilterResponse>>>(
                URLS.USER.SAVED_FILTER(domain),
                payload,
            );
            return data;
        },
        removeSavedFilter: async (domain: 'recruitment' | 'seminar') => {
            const { data } = await axios.delete(URLS.USER.SAVED_FILTER(domain));
            return data;
        },
        // 앱 기기(FCM 토큰) 등록/갱신 — Capacitor 앱에서만 호출
        registerDevice: async (payload: { platform: 'ANDROID' | 'IOS'; token: string; appVersion?: string }) => {
            const { data } = await axios.put(URLS.USER.DEVICES, payload);
            return data;
        },
        // 앱 기기 해제 — 로그아웃 시
        unregisterDevice: async (token: string) => {
            const { data } = await axios.delete(URLS.USER.DEVICE(token));
            return data;
        },
        // 푸시 알림 탭 보고 (열람 집계) — 푸시 data.logId
        reportPushOpen: async (logId: string) => {
            const { data } = await axios.post(URLS.USER.PUSH_OPENS, { logId });
            return data;
        },
        // 알림함 목록 (최신순)
        getNotifications: async (page = 0, size = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<NotificationPage>>>(
                `${URLS.USER.NOTIFICATIONS}?page=${page}&size=${size}`,
            );
            return data;
        },
        // 안 읽은 알림 수 (헤더 배지). silent: 401 시 리다이렉트 없이 조용히 실패
        getUnreadNotificationCount: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{ count: number }>>>(
                URLS.USER.NOTIFICATIONS_UNREAD_COUNT,
                { skipAuthHandler: true } as any,
            );
            return data;
        },
        markNotificationRead: async (id: string) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<NotificationItem>>>(URLS.USER.NOTIFICATION_READ(id));
            return data;
        },
        markAllNotificationsRead: async () => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<{ updated: number }>>>(URLS.USER.NOTIFICATIONS_READ_ALL);
            return data;
        },
        // 로그인 상태 비밀번호 변경. 성공 시 서버에서 refresh token 무효화 → 다른 디바이스 자동 로그아웃
        changePassword: async (payload: { currentPassword: string; newPassword: string }) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<null>>>(
                URLS.USER.CHANGE_PASSWORD,
                payload,
            );
            return data;
        },
        // 회원 탈퇴 (Soft Delete — 30일 복구 기간 뒤 익명화). reason은 WithdrawalReason enum 이름
        withdraw: async (payload: { password: string; reason: string; reasonDetail?: string }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<null>>>(
                URLS.USER.WITHDRAW,
                payload,
            );
            return data;
        },
        // 내 면허 인증 상태 조회. NONE 이면 프로필 값(이름·생년월일·면허번호)이 프리필로 내려온다.
        getLicenseVerification: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<LicenseVerificationResponse>>>(
                URLS.USER.LICENSE_VERIFICATION,
            );
            return data;
        },
        // 면허 인증 신청 (multipart). 이미지는 필수(없으면 서버 400)이며, 검수 완료 즉시 서버에서 파기된다.
        // jpg·png 는 화면(LicenseVerificationPage)에서 첨부 시점에 압축(compressLicenseImage, 긴 변 2400px)된 파일이 넘어오고,
        // pdf 는 원본 그대로다. base64 가 아닌 multipart 바이너리로 올린다(base64 는 약 33% 커진다).
        submitLicenseVerification: async (payload: LicenseVerificationSubmitPayload) => {
            const formData = new FormData();
            formData.append('name', payload.name);
            formData.append('birthDate', payload.birthDate);
            formData.append('licenseNumber', payload.licenseNumber);
            formData.append('consent', String(payload.consent));
            if (payload.image) {
                formData.append('image', payload.image, payload.image.name);
            }
            formData.append('autoCheckOptOut', String(payload.autoCheckOptOut ?? false));

            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<LicenseVerificationSubmitResult>>>(
                URLS.USER.LICENSE_VERIFICATION,
                formData,
            );
            return data;
        },
    },

    auth: {
        // 이메일이 로그인 식별자다. payload: { email, password }
        login: async (payload: { email: string; password: string }) => {
            const { data } = await axios.post(URLS.USER.LOGIN, payload);
            return data;
        },
        // Access token 재발급. body: { email }
        refresh: async (email: string) => {
            const { data } = await axios.post(URLS.USER.REFRESH, { email });
            return data;
        },
        logout: async () => {
            // 앱이면 로그아웃 전에(인증 쿠키가 살아 있을 때) 이 기기의 푸시 토큰을 계정에서 떼어낸다.
            await unregisterPushDevice();
            const { data } = await axios.post(URLS.USER.LOGOUT);
            return data;
        },
        verifyIdentity: async (payload: { id: string; name: string; email?: string; phoneNumber?: string }) => {
            const { data } = await axios.post(URLS.AUTH.PASSWORD_VERIFY, payload);
            return data;
        },
        resetPassword: async (payload: { id: string; name: string; email?: string; phoneNumber?: string; newPassword: string }) => {
            const { data } = await axios.post(URLS.AUTH.PASSWORD_RESET, payload);
            return data;
        },
        // 이메일 6자리 코드 방식
        passwordResetRequest: async (name: string, email: string) => {
            const { data } = await axios.post(URLS.AUTH.PASSWORD_RESET_REQUEST, { name, email });
            return data;
        },
        passwordResetVerify: async (email: string, code: string) => {
            // 코드 틀림(401)은 화면이 직접 안내한다 — 공용 401 처리(로그인 화면으로 보내기)를 건너뛴다
            const { data } = await axios.post(URLS.AUTH.PASSWORD_RESET_VERIFY, { email, code }, { skipAuthHandler: true });
            return data;
        },
        passwordResetConfirm: async (resetToken: string, newPassword: string) => {
            // 토큰 만료·무효(401)는 화면이 직접 안내한다 — 공용 401 처리(로그인 화면으로 보내기)를 건너뛴다
            const { data } = await axios.post(URLS.AUTH.PASSWORD_RESET_CONFIRM, { resetToken, newPassword }, { skipAuthHandler: true });
            return data;
        },
        naverLogin: async (payload: { code: string; state: string }) => {
            const { data } = await axios.post(URLS.AUTH.NAVER_LOGIN, payload);
            return data;
        },
        // 탈퇴 유예 계정을 네이버로 복구+연동 (code 필드에 access token 전달)
        naverRestore: async (accessToken: string) => {
            const { data } = await axios.post(URLS.AUTH.NAVER_RESTORE, { code: accessToken });
            return data;
        },
        kakaoLogin: async (payload: { code: string }) => {
            const { data } = await axios.post(URLS.AUTH.KAKAO_LOGIN, payload);
            return data;
        },
        // 탈퇴 유예 계정을 카카오로 복구+연동 (code 필드에 access token 전달)
        kakaoRestore: async (accessToken: string) => {
            const { data } = await axios.post(URLS.AUTH.KAKAO_RESTORE, { code: accessToken });
            return data;
        },
        // Apple 로그인 — 웹(폼 콜백으로 받은 id_token)·iOS 네이티브 공통.
        appleLogin: async (payload: AppleLoginRequest) => {
            const { data } = await axios.post(URLS.AUTH.APPLE_LOGIN, payload);
            return data;
        },
        // 탈퇴 유예 계정을 Apple 로 복구+연동 (로그인 응답의 appleTicket 전달)
        appleRestore: async (ticket: string) => {
            const { data } = await axios.post(URLS.AUTH.APPLE_RESTORE, { ticket });
            return data;
        },
        // 탈퇴 후 30일 유예 기간 내 계정 복구. 성공 시 access token 쿠키 자동 설정 + refresh token 반환.
        restoreAccount: async (idOrEmail: string, password: string) => {
            const { data } = await axios.post(URLS.AUTH.RESTORE, { idOrEmail, password });
            return data;
        },
    },

    // 회원가입 이메일 인증 (공개 엔드포인트). 소셜(네이버) 가입은 인증 면제.
    emailVerification: {
        // 6자리 인증코드를 이메일로 전송
        send: async ({ email }: { email: string }) => {
            const { data } = await axios.post(URLS.AUTH.EMAIL_VERIFICATION_SEND, { email });
            return data;
        },
        // 입력한 코드 검증 → 인증 완료 처리
        verify: async ({ email, code }: { email: string; code: string }) => {
            const { data } = await axios.post(URLS.AUTH.EMAIL_VERIFICATION_VERIFY, { email, code });
            return data;
        },
    },

    reply: {
        update: async (postId:string, commentId:string, replyId:string, payload: ReplyRequest) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.UPDATE_REPLY(postId, commentId, replyId),
                payload,
            );
            return data;
        },

        create: async (postId:string, commentId:string, payload: ReplyRequest) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.CREATE_REPLY(postId, commentId),
                payload,
            );
            return data;
        },
        delete: async (postId:string, commentId:string, replyId:string) => {
            const { data } = await axios.delete<any, AxiosResponse>(
                URLS.POST.DELETE_REPLY(postId, commentId, replyId),
            );
            return data;
        },
    },
    post: {
        getList: async (limit: number, offset: number, filters?: { search?: string; tag?: string }) => {
            const params: any = { limit, offset };
            if (filters?.search) params.search = filters.search;
            if (filters?.tag) params.tag = filters.tag;
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                '/api/posts',
                { params }
            );
            return data;
        },
        get: async (postId: string, limit?: number) => {
            const params: any = {};
            if (limit) {
                params.limit = limit;
            }
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.GET_POST(postId),
                { params }
            );
            return data;
        },
        // 글쓰기 진입 전 도배 방지 제한 여부 확인 (제한 중이면 blocked=true + 남은 시간)
        writeLimit: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.WRITE_LIMIT,
            );
            return data;
        },
        // 속닥속닥 익명 이름(치과 계정만 target=true) — 처음 부르면 서버가 이름을 만든다
        alias: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SokdakAlias>>>(URLS.POST.ALIAS);
            return data;
        },
        // 익명 이름 바꾸기 — 규칙 위반 400·겹침/30일 409 는 message 를 칸 아래에 보여 준다
        renameAlias: async (nickname: string) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<SokdakAlias>>>(URLS.POST.ALIAS, { nickname });
            return data;
        },
        create: async (payload: PostRequest) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.CREATE,
                payload,
            );
            return data;
        },
        update: async (postId: string, payload: PostRequest) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.UPDATE_POST(postId),
                payload,
            );
            return data;
        },
        delete: async (postId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.DELETE_POST(postId)
            );
            return data;
        },
        view: async (postId: string) => {
            await axios
                .patch<any, AxiosResponse>(URLS.POST.VIEW_POST(postId))
                .catch((e) => {
                    console.error(e);
                });
        },
        getMyPosts: async (offset: number = 0, limit: number = 10) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.MY_POSTS,
                { params: { offset, limit } }
            );
            return data;
        },
        toggleLike: async (postId: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<{ liked: boolean; likeCount: number }>>>(
                URLS.POST.TOGGLE_LIKE(postId),
            );
            return data;
        },
        // 댓글·답글 좋아요 토글 — replyId 가 있으면 답글
        toggleCommentLike: async (postId: string, commentId: string, replyId?: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<{ liked: boolean; likeCount: number }>>>(
                replyId ? URLS.POST.TOGGLE_REPLY_LIKE(postId, commentId, replyId) : URLS.POST.TOGGLE_COMMENT_LIKE(postId, commentId),
            );
            return data;
        },
    },
    notice: {
        getList: async (limit: number, offset: number, search?: string) => {
            const params: any = { limit, offset };
            if (search) params.search = search;
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                '/api/notices',
                { params }
            );
            return data;
        },
        get: async (noticeId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.NOTICE.GET_NOTICE(noticeId),
            );
            return data;
        },
        addView: async (noticeId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.NOTICE.INCREASE_VIEW(noticeId),
            );
            return data;
        },
    },
    banner: {
        /** 페이지 상단·하단 배너 — 여러 위치를 한 번에(결과: 위치 이름 → 배너 목록). 비로그인도 된다 */
        getPageBanners: async (locations: string[]) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Record<string, any[]>>>>(
                '/api/banners',
                { params: { locations: locations.join(',') } }
            );
            return data;
        },
    },
    faq: {
        /** 자주 묻는 질문 — 분류·검색어는 선택. 비로그인도 된다 */
        list: async (group?: FaqGroupCode, q?: string) => {
            const params: Record<string, string> = {};
            if (group) params.group = group;
            if (q && q.trim()) params.q = q.trim();
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<FaqListResponse>>>('/api/faqs', { params });
            return data;
        },
    },
    announcement: {
        // 현재 활성화된 상단 띠 문구 조회 (없으면 result=null)
        getTop: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                id: number;
                content: string;
                updatedAt: string;
            } | null>>>(URLS.ANNOUNCEMENT.TOP);
            return data;
        },
    },
    comment: {
        update: async (postId:string, commentId:string, payload: CommentRequest) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.UPDATE_COMMENT(postId, commentId),
                payload,
            );
            return data;
        },

        delete: async (postId:string, commentId:string) => {
            const { data } = await axios.delete<any, AxiosResponse>(
                URLS.POST.DELETE_COMMENT(postId, commentId),
            );
            return data;
        },

        create: async (postId:string, payload: CommentRequest) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.POST.CREATE_COMMENT(postId),
                payload,
            );
            return data;
        },
    },

    search: {
        home: async () => {
            const { data } = await axios.get(URLS.SEARCH.HOME);
            return data;
        },
        homeFeed: async () => {
            const { data } = await axios.get<ValidateResponse<HomeFeed>>(URLS.SEARCH.HOME_RECRUITMENTS, {
                headers: { 'X-Home-Layout': 'unified' },
            });
            return data;
        },
        homeSummary: async () => {
            const { data } = await axios.get(URLS.SEARCH.HOME_SUMMARY);
            return data;
        },
        homeComponent: async () => {
            const { data } = await axios.get<ValidateResponse<HomeFeed>>(URLS.SEARCH.HOME_RECRUITMENTS, {
                headers: { 'X-Home-Layout': 'unified' },
            });
            return { ...data, result: data.result.flatMap(section => section.type === 'RECRUITMENT' ? [section.data] : []) };
        },
        homeExpertContents: async (limit: number = 5, briefingOnly: boolean = false) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any[]>>>(
                `${URLS.SEARCH.HOME_EXPERT_LOUNGE}?limit=${limit}&briefingOnly=${briefingOnly}`,
            );
            return data;
        },
        getMeta: async () => {
            const { data } = await axios.get<any, AxiosResponse>(
                URLS.SEARCH.RECRUITMENT_META,
            );
            return data;
        },

        getSubwayLines: async (areaId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SubwayStation[]>>>(
                URLS.SEARCH.SUBWAY_LINES(areaId),
            );
            return data;
        },
        getSubwayArea: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SubwayArea[]>>>(
                URLS.SEARCH.SUBWAY_AREA,
            );
            return data;
        },

        getSubwayStations: async (lineId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SubwayStation[]>>>(
                URLS.SEARCH.SUBWAY_STATIONS(lineId),
            );
            return data;
        },

        // 역 이름으로 전국 지하철역 검색(같은 이름 다른 노선은 따로 온다 — 예: 서면 부산 1호선·2호선)
        searchSubwayStations: async (q: string, limit: number = 30) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SubwayStation[]>>>(
                URLS.SEARCH.SUBWAY_STATION_SEARCH,
                { params: { q, limit } },
            );
            return data;
        },

        getCities: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<City[]>>>(
                URLS.SEARCH.GET_CITIES,
            );
            return data;
        },

        getDistricts: async (cityId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<District[]>>>(
                URLS.SEARCH.GET_DISTRICTS(cityId),
            );
            return data;
        },

        // 여러 cityId에 대한 districts를 한 번에 조회
        getDistrictsBatch: async (cityIds: string[]) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Record<string, District[]>>>>(
                URLS.SEARCH.GET_DISTRICTS_BATCH,
                {
                    params: { cityIds: cityIds.join(',') },
                }
            );
            return data;
        },

	getResumeMeta: async () => {
		const { data } = await axios.post<any, AxiosResponse<ResumeMetaType>>(URLS.SEARCH.RESUME_META);
		return data;
        },
    },

    signup: {
        usersMeta: async () => {
            const { data } = await axios.get(URLS.SIGNUP.USERS_META);
            return data;
        },
        clinicsMeta: async () => {
            const { data } = await axios.get(URLS.SIGNUP.CLINICS_META);
            return data;
        },
        /** 가입 중(이메일 인증 마친 뒤) 홈페이지에서 대표 사진 후보·대표자·주소 제안 찾기 */
        homepagePreview: async (email: string, url: string, credentials?: SignupCredentials | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<HomepagePreview>>>(
                '/api/auth/clinics/signup/homepage-preview',
                { email, url },
                signupIdentityConfig(credentials),
            );
            return data;
        },
        /** 병원 인증(이메일 인증 뒤) — 번호·대표자·개업연월일·병원 이름 */
        hospitalCheck: async (payload: { email: string; businessNumber: string; ownerName: string; openingDate: string; clinicName: string; address: string }, credentials?: SignupCredentials | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<SignupHospitalCheckResult>>>('/api/auth/clinics/signup/hospital-check', payload, signupIdentityConfig(credentials));
            return data;
        },
        /** 병원 인증 — 치과 홈페이지 주소로 확인(최대 8초) */
        homepageCheck: async (payload: { email: string; url: string; businessNumber: string; ownerName: string }, credentials?: SignupCredentials | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<SignupHomepageCheckResult>>>('/api/auth/clinics/signup/homepage-check', payload, signupIdentityConfig(credentials));
            return data;
        },
        /** 가입 중(이메일 인증 뒤) 사업자등록증 사진 올리기 — 비공개 저장, 토큰만 받아 가입 요청에 넘긴다 */
        businessCert: async (email: string, file: File, credentials?: SignupCredentials | null) => {
            const form = new FormData();
            form.append('email', email);
            form.append('file', file);
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<BusinessCertUploadResult>>>(
                '/api/auth/clinics/signup/business-cert',
                form,
                signupIdentityConfig(credentials),
            );
            return data;
        },
        /** 이미 가입된 치과 — 가입 이메일(아이디) 바꾸기 신청(이메일 인증 뒤, 사업자등록증 토큰 필수) */
        clinicEmailChangeRequest: async (payload: ClinicEmailChangeRequestPayload, credentials?: SignupCredentials | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<ClinicEmailChangeRequestResult>>>(
                '/api/auth/clinics/signup/email-change-requests',
                payload,
                signupIdentityConfig(credentials),
            );
            return data;
        },
        /** 가입 중 고른 홈페이지 사진을 서버가 내려받아 저장 → 썸네일 주소 */
        homepageThumbnail: async (email: string, imageUrl: string, credentials?: SignupCredentials | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ url: string }>>>(
                '/api/auth/clinics/signup/homepage-thumbnail',
                { email, imageUrl },
                signupIdentityConfig(credentials),
            );
            return data;
        },
    },

    apply: {
        /** jobTasks = 지원할 업무(JobTaskType 이름). 담당 업무 2개 이상 공고는 1개 이상, 1개 이하 공고는 빈 목록(서버가 채움) */
        post:async (recruitmentId: number, jobTasks: string[] = []) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RECRUITMENTS.APPLY(recruitmentId),
                { jobTasks },
            );
            return data;
        },
        get:async (limit: number, offset: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.LIST.APPLY(limit, offset),
            );
            return data;
        },
        check: async (recruitmentId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{hasApplied: boolean, hasResume: boolean}>>>(
                `/api/recruitments/${recruitmentId}/apply/check`,
            );
            return data;
        },
        /** 지원자 본인의 지원 취소 — 치과가 아직 확인하지 않은 지원만(이미 확인했으면 409 와 안내 문구) */
        cancel: async (applicationId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.APPLICATIONS.CANCEL(applicationId),
            );
            return data;
        },
        updateStatus: async (applicationId: number, status: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/applications/${applicationId}/status`,
                null,
                { params: { status } }
            );
            return data;
        },
    },

    getTableList: async (fetchUrl: string, listCount: number, offset: number) => {
        const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
            URLS.LIST.TABLE(fetchUrl, listCount, offset),
        );
        return data;
    },

    getClinicsInfo: async (id: number) => {
        const { data } = await axios.get<any, AxiosResponse<ClinicsResponse>>(URLS.CLINICS.INFO(id));
        return data;
    },

    clinic: {
        get: async (id: number) => {
            const { data } = await axios.get<any, AxiosResponse<ClinicsResponse>>(
                URLS.CLINICS.INFO(id),
            );
            return data;
        },

        put: async (id: number, payload:ModifyClinicInfoRequest) => {
            // 승인 전 치과는 저장 직후 검증 결과(ClinicReadiness)가 result 로 온다
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<ClinicReadiness | null>>>(
                URLS.CLINICS.INFO(id),
                payload
            );
            return data;
        },
        /** 승인 준비 상태(빠진 칸·마지막 검증) — 외부 접속 없음 */
        readiness: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<ClinicReadiness>>>('/api/clinics/me/readiness');
            return data;
        },
        /** 홈페이지 검증·자동 승인 시도 */
        verify: async () => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<ClinicReadiness>>>('/api/clinics/me/verify');
            return data;
        },
        /** 홈페이지로 자동 채우기 — 저장 전 입력한 주소를 읽어 제안·후보 사진 */
        homepagePreview: async (url: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<HomepagePreview>>>('/api/clinics/me/homepage-preview', { url });
            return data;
        },
        /** 후보 사진을 서버가 내려받아 저장 → 주소만(치과 이미지는 화면의 [저장]으로) */
        homepagePhoto: async (imageUrl: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ url: string }>>>('/api/clinics/me/homepage-photo', { imageUrl });
            return data;
        },
        /** 마지막 검증의 후보 사진을 대표 사진으로(치과 이미지 맨 앞에 바로 저장 — AI 대화 카드) */
        useHomepageThumbnail: async (imageUrl: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ url: string }>>>('/api/clinics/me/homepage-thumbnail', { imageUrl });
            return data;
        },
        /** 사업자등록증 사진 올리기 — 비공개 저장, 토큰만 받아 [저장] 요청에 넘긴다 */
        uploadBusinessCert: async (file: File) => {
            const form = new FormData();
            form.append('file', file);
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<BusinessCertUploadResult>>>(
                '/api/clinics/me/business-cert',
                form,
            );
            return data;
        },
        /** 확인 카드 저장(회원이 [저장]을 눌렀을 때만) → 다시 검증한 결과 */
        saveFields: async (fields: Record<string, string>) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<ClinicReadiness>>>('/api/clinics/me/fields', { fields });
            return data;
        },
    },

    register: {
        clinic: async (payload: RecruimentCreationRequest) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RECRUITMENTS.CLINIC_REGISTER,
                payload,
            );
            return data;
        },
        resume: async (payload: ResumeRegisterType) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RESUME.REGISTER,
                payload,
            );
            return data;
        },
    },

    modify: {
        clinic: async (recruitmentId: number, payload: RecruimentCreationRequest) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/recruitments/${recruitmentId}`,
                payload,
            );
            return data;
        },
    },

    resume: {
        get: async () => {
            const { data } = await axios.get<any, AxiosResponse<ResumeViewResponse>>(
                URLS.RESUME.GET,
            );
            return data;
        },
        getById: async (resumeId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ResumeViewResponse>>(
                URLS.RESUME.GET_BY_ID(resumeId),
            );
            return data;
        },
        getByApplicationId: async (applicationId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ApplicantResumeViewResponse>>(
                URLS.RESUME.GET_BY_APPLICATION(applicationId),
            );
            return data;
        },
        update: async (payload: ResumeRegisterType) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RESUME.UPDATE,
                payload,
            );
            return data;
        },
    },

    recommend: {
        get: async (limit: number, offset: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.RECRUITMENTS.RECOMMEND(limit, offset),
            );
            return data;
        },
    },

    recruitmentComponents: {
        getWelfareChips: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                '/api/recruitment/components/welfare'
            );
            return data;
        },
        // 복지 채용공고 통합 API - 모든 복지 타입의 채용공고를 한 번에 조회
        getAllWelfareRecruitments: async (limit: number = 6) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                welfareChips: Array<{ label: string; value: string }>;
                welfareRecruitments: Record<string, { label: string; list: any[] }>;
            }>>>('/api/recruitments/welfare/all', { params: { limit } });
            return data;
        },
        // 채용목록 페이지 초기화 통합 API
        getRecruitmentPageInit: async (welfareLimit: number = 6) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                meta: {
                    jobTasks: Array<{ description: string; type: string }>;
                    careerType: Array<{ description: string; type: string }>;
                    workingDaysType: Array<{ description: string; type: string }>;
                    recruitmentType: Array<{ description: string; type: string }>;
                };
                cities: Array<{ id: string; name: string }>;
                welfareChips: Array<{ label: string; value: string }>;
                welfareRecruitments: Record<string, { label: string; list: any[] }>;
                banners?: any;
            }>>>('/api/recruitments/init', { params: { welfareLimit } });
            return data;
        },
    },

    seminar: {
        create: async (payload: any) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.CREATE,
                payload,
            );
            return data;
        },
        // 마이페이지 결제내역 — 본인 세미나 결제/환불 내역(최신순, 환불 이력 포함) 페이지 조회
        getPaymentHistory: async (page: number = 0, size: number = 10) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.PAYMENT_HISTORY,
                { params: { page, size } }
            );
            return data;
        },
        getList: async (limit: number, offset: number, filters?: any) => {
            const params: any = { limit, offset };
            if (filters?.categories) params.categories = filters.categories;
            if (filters?.levels) params.levels = filters.levels;
            if (filters?.startDate) params.startDate = filters.startDate;
            if (filters?.endDate) params.endDate = filters.endDate;
            if (filters?.nearbyStations) params.nearbyStations = filters.nearbyStations;
            if (filters?.search) params.search = filters.search;
            // 목록 탭(전체·커리어·외부)과 "전체" 커리어 우선 정렬 — 서버가 모르는 값이면 무시된다
            if (filters?.seminarType) params.seminarType = filters.seminarType;
            if (filters?.sort) params.sort = filters.sort;
            // 진행 방식(OFFLINE·LIVE·VOD·ONLINE 쉼표)·무료만 — docs/seminar-filter-mobile-plan.md 1장
            if (filters?.formats) params.formats = filters.formats;
            if (filters?.freeOnly) params.freeOnly = filters.freeOnly;

            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.GET_LIST,
                { params }
            );
            return data;
        },
        getDetail: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.GET_DETAIL(seminarId),
            );
            return data;
        },
        update: async (seminarId: string, payload: any) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.UPDATE(seminarId),
                payload,
            );
            return data;
        },
        // 커리어 라이브·동영상 접속 링크·안내만 변경 — 시작 후에도 종료 전까지 가능(강사 본인). 링크가 바뀌면 참가자 알림
        updateOnlineAccess: async (seminarId: string, payload: { onlineUrl: string; onlineGuide: string | null }) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                `${URLS.SEMINAR.UPDATE(seminarId)}/online-access`,
                payload,
            );
            return data;
        },
        // 접속하기 — 참가 확정자가 누를 때 최초 1회 접속 시각을 기록하고 접속 정보를 받는다(동영상은 이후 환불 불가)
        openOnlineAccess: async (seminarId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<SeminarOnlineAccessOpenResult>>>(
                `${URLS.SEMINAR.UPDATE(seminarId)}/online-access/open`,
            );
            return data;
        },
        // 상시 동영상 판매 종료·재개(강사 본인) — 이미 결제한 참가자는 계속 볼 수 있다
        setSalesClosed: async (seminarId: string, closed: boolean) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                `${URLS.SEMINAR.UPDATE(seminarId)}/sales-closed`,
                { closed },
            );
            return data;
        },
        delete: async (seminarId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.DELETE(seminarId),
            );
            return data;
        },
        apply: async (seminarId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.APPLY(seminarId),
            );
            return data;
        },
        // 유료 세미나 결제: 주문 생성(금액 서버 고정). 쿠폰을 고르면 그 발급 행을 예약하고 amount = 실결제
        paymentPrepare: async (seminarId: string, couponIssueId?: number | null) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<SeminarPaymentPrepareResult>>>(
                `/api/seminars/${seminarId}/payments/prepare`,
                couponIssueId ? { couponIssueId } : {},
            );
            return data;
        },
        // 유료 세미나 결제: 승인 + 참가 등록
        paymentConfirm: async (seminarId: string, payload: { paymentKey: string; orderId: string; amount: number }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/seminars/${seminarId}/payments/confirm`,
                payload,
            );
            return data;
        },
        // 강사 월 정산 — 정산 예정 + 월별 정산서
        // silent: 메뉴 노출을 정하려고 조용히 물어보는 호출 — 401 이어도 로그인 화면으로 보내지 않는다
        settlementOverview: async (options?: { silent?: boolean }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<InstructorSettlementOverview>>>(
                `/api/seminar/instructor/settlements`,
                options?.silent ? { skipAuthHandler: true } : undefined,
            );
            return data;
        },
        settlementDetail: async (settlementId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<InstructorSettlement>>>(
                `/api/seminar/instructor/settlements/${settlementId}`,
            );
            return data;
        },
        settlementObjection: async (settlementId: number, reason: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<InstructorSettlement>>>(
                `/api/seminar/instructor/settlements/${settlementId}/objection`,
                { reason },
            );
            return data;
        },
        // 결제 전·후 환불 일정 — 결제 전이면 지금 결제할 때 기준, 결제했으면 결제 시각 기준
        refundSchedule: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                paid: boolean;
                paidAt?: string;
                seminarStart: string;
                windows: { until: string; refundRate: number; basis: string }[];
                lines: string[];
                /** 동영상 — 본인이 처음 접속한 시각(없으면 아직 안 열었다) */
                accessedAt?: string | null;
                /** 동영상 — "접속 링크를 열면 환불할 수 없어요." */
                accessNotice?: string | null;
            }>>>(`/api/seminars/${seminarId}/payments/refund-schedule`);
            return data;
        },
        // 취소 시 환불 견적(기간별 환불율) 조회
        refundQuote: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                hasPayment: boolean;
                cancellable?: boolean;
                refundRate?: number;
                originalAmount?: number;
                refundAmount?: number;
                reason?: string;
                /** 쿠폰 결제면 — 이번 취소로 쿠폰이 돌아오는지(없으면 null) */
                coupon?: RefundQuoteCoupon | null;
            }>>>(`/api/seminars/${seminarId}/payments/refund-quote`);
            return data;
        },
        cancel: async (seminarId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.CANCEL(seminarId),
            );
            return data;
        },
        createReview: async (seminarId: string, payload: { content: string; rating: number }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEWS(seminarId),
                payload
            );
            return data;
        },
        updateReview: async (seminarId: string, reviewId: string, payload: { content: string; rating: number }) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEW_DETAIL(seminarId, reviewId),
                payload
            );
            return data;
        },
        deleteReview: async (seminarId: string, reviewId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEW_DETAIL(seminarId, reviewId)
            );
            return data;
        },
        createReviewReply: async (seminarId: string, reviewId: string, payload: { content: string }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEW_REPLIES(seminarId, reviewId),
                payload
            );
            return data;
        },
        updateReviewReply: async (seminarId: string, reviewId: string, replyId: string, payload: { content: string }) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEW_REPLY_DETAIL(seminarId, reviewId, replyId),
                payload
            );
            return data;
        },
        deleteReviewReply: async (seminarId: string, reviewId: string, replyId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REVIEW_REPLY_DETAIL(seminarId, reviewId, replyId)
            );
            return data;
        },
        getApplied: async (page: number = 0, size: number = 10) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.APPLIED,
                { params: { page, size } }
            );
            return data;
        },
        getMyRegistered: async (page: number = 0, size: number = 10) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.MY_REGISTERED,
                { params: { page, size } }
            );
            return data;
        },
        getParticipants: async (seminarId: string, opts?: { skipAuthHandler?: boolean }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.PARTICIPANTS(seminarId),
                { skipAuthHandler: opts?.skipAuthHandler } as any
            );
            return data;
        },
        approveParticipant: async (seminarId: string, participantNo: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.APPROVE_PARTICIPANT(seminarId, participantNo)
            );
            return data;
        },
        rejectParticipant: async (seminarId: string, participantNo: string, reason?: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REJECT_PARTICIPANT(seminarId, participantNo),
                reason ? { reason } : {}
            );
            return data;
        },
        markAttended: async (seminarId: string, participantNo: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.MARK_ATTENDED(seminarId, participantNo)
            );
            return data;
        },
        closeRecruitment: async (seminarId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.CLOSE(seminarId)
            );
            return data;
        },
        reopenRecruitment: async (seminarId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.REOPEN(seminarId)
            );
            return data;
        },
        // 세미나 폐강 (사유 필수) — 결제 참가자 전원 환불 + 메일/카톡 통지
        cancelSeminar: async (seminarId: string, reason: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/seminars/${seminarId}/cancel`,
                { reason }
            );
            return data;
        },
        getByInstructor: async (instructorId: string, page: number = 0, size: number = 100) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/seminars/instructor/${instructorId}`,
                { params: { page, size } }
            );
            return data;
        },
        getByUserNo: async (userNo: string, page: number = 0, size: number = 100) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.BY_USER(userNo),
                { params: { page, size } }
            );
            return data;
        },
        updateResumeVisibility: async (seminarId: string, isHidden: boolean) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.SEMINAR.RESUME_VISIBILITY(seminarId),
                { isHidden }
            );
            return data;
        }
    },

    /** 세미나 쿠폰 (docs/seminar-coupon-impl-plan.md 3절) — 서버에 아직 없으면 호출부가 '쿠폰 없음'으로 폴백한다 */
    coupon: {
        // 이 세미나에 받을 수 있는 쿠폰(공개 — 로그인이면 claimed 포함). 비로그인 조회라 401 처리 생략
        listForSeminar: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SeminarClaimableCoupon[]>>>(
                `/api/seminars/${seminarId}/coupons`,
                { skipAuthHandler: true },
            );
            return data;
        },
        claim: async (seminarId: string, couponId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/seminars/${seminarId}/coupons/${couponId}/claim`,
            );
            return data;
        },
        claimByCode: async (code: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<ClaimByCodeResult>>>(
                `/api/coupons/claim-by-code`,
                { code },
            );
            return data;
        },
        mine: async (status: MyCouponStatus | 'USED' = 'AVAILABLE', opts?: { skipAuthHandler?: boolean }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<MyCoupon[] | { list: MyCoupon[] }>>>(
                `/api/me/coupons`,
                { params: { status }, skipAuthHandler: opts?.skipAuthHandler },
            );
            return data;
        },
        // MY 메뉴·드로어 "쿠폰 N장" — {available: N}
        count: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{ available: number }>>>(
                `/api/me/coupons/count`,
                { skipAuthHandler: true },
            );
            return data;
        },
        // 결제 모달 — 내 쿠폰 중 이 세미나에 쓸 수 있는 것 + 할인액 + best(최대 할인)
        applicable: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<ApplicableCouponsResponse>>>(
                `/api/seminars/${seminarId}/coupons/applicable`,
                { skipAuthHandler: true },
            );
            return data;
        },
        // ── 강사(본인 세미나만) ──
        instructorList: async (seminarId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<InstructorCoupon[]>>>(
                `/api/seminar/instructor/seminars/${seminarId}/coupons`,
            );
            return data;
        },
        instructorCreate: async (seminarId: string, payload: InstructorCouponPayload) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<InstructorCoupon>>>(
                `/api/seminar/instructor/seminars/${seminarId}/coupons`,
                payload,
            );
            return data;
        },
        instructorUpdate: async (couponId: number, payload: Partial<InstructorCouponPayload>) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<InstructorCoupon>>>(
                `/api/seminar/instructor/coupons/${couponId}`,
                payload,
            );
            return data;
        },
        instructorPause: async (couponId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<InstructorCoupon>>>(
                `/api/seminar/instructor/coupons/${couponId}/pause`,
            );
            return data;
        },
        instructorResume: async (couponId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<InstructorCoupon>>>(
                `/api/seminar/instructor/coupons/${couponId}/resume`,
            );
            return data;
        },
    },

    instructor: {
        apply: async (payload: any) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INSTRUCTOR.APPLY,
                payload,
            );
            return data;
        },
        // 강사명 중복검사 (사용 가능하면 result=true)
        validateName: async (name: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<boolean>>>(
                `/api/seminar/instructor/name/validate?name=${encodeURIComponent(name)}`,
            );
            return data;
        },
        getMyProfile: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INSTRUCTOR.ME,
            );
            return data;
        },
        updateMyProfile: async (payload: {
            experience: string;
            selfIntroduction: string;
            portfolioUrl?: string | null;
            profileImageUrl?: string | null;
            specialties: string[];
            settlementBank?: string | null;
            settlementAccount?: string | null;
            settlementHolder?: string | null;
            termsAgreed?: boolean;
            termsVersion?: string | null;
        }) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INSTRUCTOR.UPDATE_ME,
                payload,
            );
            return data;
        },
        getMyApplications: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INSTRUCTOR.MY_APPLICATIONS,
            );
            return data;
        },
        getApplication: async (id: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INSTRUCTOR.GET_APPLICATION(id),
            );
            return data;
        },
    },

    upload: {
        /**
         * purpose 'seminar-description' — 세미나 설명 에디터 전용. 세로가 가로×1.5 를 넘는 긴 이미지면 서버가 원본 옆에
         * 위쪽 3:4 파생본(`{원본}_top.jpg`)을 만들고 topUrl 로 돌려준다(없으면 null). 다른 곳은 purpose 없이 그대로.
         */
        image: async (file: File, options?: { purpose?: 'seminar-description' | 'recruitment-description' }) => {
            // 해상도·화질은 유지하고 용량만 줄인다(PNG 는 WebP 로 변환). 한도를 넘으면 요청 없이 ImageTooLargeError
            const compressed = await prepareImageForUpload(file);

            const formData = new FormData();
            formData.append('image', compressed, compressed.name);

            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ url: string; topUrl?: string | null }>>>(
                URLS.UPLOAD.IMAGE,
                formData,
                options?.purpose ? { params: { purpose: options.purpose } } : undefined,
            );
            return data;
        },
        file: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file, file.name);

            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ url: string; name: string }>>>(
                URLS.UPLOAD.FILE,
                formData,
            );
            return data;
        },
    },

    // 이벤트 경품 받기(당첨자 본인) — 남의 건·없는 건은 404
    prize: {
        me: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<PrizeItem[]>>>(URLS.PRIZE.ME);
            return data;
        },
        submit: async (id: string, payload: PrizeSubmitPayload) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<PrizeItem>>>(URLS.PRIZE.SUBMIT(id), payload);
            return data;
        },
        depositor: async (id: string, depositorName: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<PrizeItem>>>(URLS.PRIZE.DEPOSITOR(id), { depositorName });
            return data;
        },
        forfeit: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<PrizeItem>>>(URLS.PRIZE.FORFEIT(id));
            return data;
        },
        received: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<PrizeItem>>>(URLS.PRIZE.RECEIVED(id));
            return data;
        },
    },

    inquiry: {
        // 문의 카테고리 목록
        getCategories: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{ code: string; displayName: string }>>>>(
                URLS.INQUIRY.CATEGORIES,
            );
            return data;
        },
        // 문의 등록
        create: async (params: { category: string; title: string; contents: string; imageUrls?: string[]; applicationId?: number }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INQUIRY.CREATE,
                params,
            );
            return data;
        },
        // 내 문의 내역
        getMy: async (page: number = 0, size: number = 10) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{ list: any[]; total: number }>>>(
                `${URLS.INQUIRY.MY}?page=${page}&size=${size}`,
            );
            return data;
        },
        // 내 문의 상세
        getDetail: async (id: string) => {
            // 공용 401 처리(로그인으로 보내기)를 끈다 — 로그인한 채 남의 문의 주소를 열면 로그인 화면이 원래 주소로 되돌려
            // 401 ↔ 로그인 사이를 끝없이 오갔다(2026-10-07 리뷰). 화면이 직접 "볼 수 없어요" 로 처리한다
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INQUIRY.DETAIL(id),
                { skipAuthHandler: true },
            );
            return data;
        },
        // 문의 대화 — 메시지 보내기(글·사진 3장까지) → 갱신된 상세
        sendMessage: async (id: string, params: { text: string; imageUrls: string[] }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INQUIRY.MESSAGES(id),
                params,
            );
            return data;
        },
        // 문의 완료 → 갱신된 상세(완료 뒤엔 더 보낼 수 없다)
        close: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.INQUIRY.CLOSE(id),
            );
            return data;
        },
    },

    searchKeyword: {
        // 통합검색 탭별 결과 수 — 결과 많은 탭 우선 정렬/타겟팅용
        counts: async (keyword: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                notice: number, recruitment: number, post: number, seminar: number, expertLounge: number
            }>>>('/api/search/counts', { params: { keyword } });
            return data;
        },
        // 통합 조회 (최근 + 인기 검색어)
        getAll: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                recent: string[],
                popular: Array<{keyword: string, count: number, rank: number}>
            }>>>('/api/search/keywords');
            return data;
        },
        // 검색어 저장
        save: async (keyword: string, resultCount?: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<string>>>(
                '/api/search/keywords/recent',
                { keyword, resultCount: resultCount || 0 }
            );
            return data;
        },
        // 특정 검색어 삭제
        delete: async (keyword: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<string>>>(
                '/api/search/keywords/recent',
                { params: { keyword } }
            );
            return data;
        },
        // 모든 검색어 삭제
        deleteAll: async () => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<string>>>(
                '/api/search/keywords/recent/all'
            );
            return data;
        },
    },

    payment: {
        // 클리닉 권한 조회
        getMyRights: async (clinicId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                `/api/clinics/${clinicId}/rights`
            );
            return data;
        },
        // 채용공고 등록 권한 확인
        checkRecruitmentRight: async (clinicId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                hasRight: boolean;
                rights: Array<{
                    id: number;
                    rightType: string;
                    productName: string;
                    quantity: number;
                    startDate: string;
                    endDate: string | null;
                    status: string;
                    daysRemaining: number | null;
                }>;
            }>>>(`/api/clinics/${clinicId}/rights/check`);
            return data;
        },
        // 카테고리별 기존 권한 확인 (상품 구매 전 재구매 확인용)
        checkRightByCategory: async (clinicId: number, category: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                hasRight: boolean;
                rights: Array<{
                    id: number;
                    rightType: string;
                    productName: string;
                    quantity: number;
                    startDate: string;
                    endDate: string | null;
                    status: string;
                    daysRemaining: number | null;
                }>;
            }>>>(`/api/clinics/${clinicId}/rights/category/${category}`);
            return data;
        },
        // 상품 목록 조회
        getProducts: async (category?: string) => {
            const params: any = {};
            if (category) params.category = category;
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{
                id: number;
                name: string;
                category: string;
                price: number;
                durationDays: number | null;
                description: string | null;
            }>>>>('/api/products', { params });
            return data;
        },
        // 상품 구매
        purchase: async (clinicId: number, productId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                paymentId: number;
                rightId: number;
                message: string;
            }>>>(`/api/clinics/${clinicId}/payments`, { productId });
            return data;
        },
        // 구매내역 조회
        getPaymentHistory: async (clinicId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{
                id: number;
                rightId: number;
                productName: string;
                productCategory: string;
                amount: number;
                paymentStatus: string;
                paidAt: string;
                rightStatus: string;
                startDate: string;
                endDate: string | null;
                rightQuantity: number;
                daysRemaining: number | null;
                canRefund: boolean;
            }>>>>(`/api/clinics/${clinicId}/payments/history`);
            return data;
        },
        // 환불 요청
        refund: async (clinicId: number, rightId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                paymentId: number;
                message: string;
            }>>>(`/api/clinics/${clinicId}/rights/${rightId}/refund`);
            return data;
        },
    },

    point: {
        // 어드민 기능 공개 설정을 읽어 커리어 유형·결제 UI에 반영한다.
        getFeatureFlags: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                point: { enabled: boolean };
                seminarPayment?: { enabled: boolean; historyVisible?: boolean };
            }>>>('/api/points/features', { skipAuthHandler: true });
            return data;
        },
        // 포인트 잔액 조회
        getBalance: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                paidPoints: number;
                freePoints: number;
                totalPoints: number;
                totalCharged: number;
                totalUsed: number;
            }>>>('/api/points/balance');
            return data;
        },
        // 포인트 상품 목록 조회 (포인트로 구매 가능한 상품)
        getProducts: async (targetRole?: string) => {
            const params: any = {};
            if (targetRole) params.targetRole = targetRole;
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{
                id: number;
                type: string;
                name: string;
                description: string | null;
                pointPrice: number;
                durationDays: number | null;
                targetRole: string | null;
                isPeriodBased: boolean;
            }>>>>('/api/points/products', { params });
            return data;
        },
        // 포인트 충전 패키지 목록 조회
        getPackages: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{
                id: number;
                name: string;
                description: string | null;
                chargePoints: number;
                bonusPoints: number;
                totalPoints: number;
                price: number;
            }>>>>('/api/points/packages');
            return data;
        },
        // 포인트로 상품 구매
        purchase: async (productId: number, itemId?: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                purchaseId: number;
                usageId: number;
                balance: {
                    paidPoints: number;
                    freePoints: number;
                    totalPoints: number;
                };
                message: string;
            }>>>('/api/points/purchases', { productId, itemId });
            return data;
        },
        // 특정 권한 보유 여부 확인
        checkRight: async (productType: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                hasRight: boolean;
                rights: Array<{
                    id: number;
                    productType: string;
                    productName: string;
                    validFrom: string | null;
                    validUntil: string | null;
                    status: string;
                    isValid: boolean;
                }>;
            }>>>('/api/points/rights/check', { params: { productType } });
            return data;
        },
        // 포인트 충전 시작 (PG 결제 전)
        initiateCharge: async (packageId: number, paymentMethod?: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                chargeId: number;
                orderId: string;
            }>>>('/api/points/charges', { packageId, paymentMethod });
            return data;
        },
        // 포인트 충전 완료 (PG 결제 후)
        completeCharge: async (orderId: string, pgProvider: string, pgTransactionId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                chargeId: number;
                balance: {
                    paidPoints: number;
                    freePoints: number;
                    totalPoints: number;
                };
                message: string;
            }>>>(`/api/points/charges/${orderId}/complete`, { pgProvider, pgTransactionId });
            return data;
        },
        // 충전 내역 조회
        getChargeHistory: async (page: number = 0, size: number = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                list: Array<{
                    id: number;
                    packageName: string;
                    chargePoints: number;
                    bonusPoints: number;
                    price: number;
                    status: string;
                    createdAt: string;
                    canRefund: boolean;
                    refundablePoints: number;
                    refundDeniedReason: string | null;
                    usedFreePoints: number;
                    remainingPaidPoints: number;
                }>;
                total: number;
                page: number;
                size: number;
            }>>>('/api/points/charges', { params: { page, size } });
            return data;
        },
        // 충전 환불
        refundCharge: async (chargeId: number, reason?: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                refundedPoints: number;
                refundedAmount: number;
                balance: {
                    paidPoints: number;
                    freePoints: number;
                    totalPoints: number;
                };
                message: string;
            }>>>(`/api/points/charges/${chargeId}/refund`, { reason });
            return data;
        },
        // 구매 내역 조회
        getPurchaseHistory: async (page: number = 0, size: number = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                list: Array<{
                    id: number;
                    productType: string;
                    productName: string;
                    paidPointsUsed: number;
                    freePointsUsed: number;
                    totalPoints: number;
                    itemId: number | null;
                    validFrom: string | null;
                    validUntil: string | null;
                    status: string;
                    isValid: boolean;
                    canRefund: boolean;
                    createdAt: string;
                }>;
                total: number;
                page: number;
                size: number;
            }>>>('/api/points/purchases', { params: { page, size } });
            return data;
        },
        // 구매 환불
        refund: async (purchaseId: number, reason?: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                refundedFreePoints: number;
                refundedPaidPoints: number;
                balance: {
                    paidPoints: number;
                    freePoints: number;
                    totalPoints: number;
                };
                message: string;
            }>>>(`/api/points/purchases/${purchaseId}/refund`, { reason });
            return data;
        },
        // 포인트 거래 내역 조회
        getTransactions: async (page: number = 0, size: number = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                list: Array<{
                    id: number;
                    type: string;
                    amount: number;
                    balanceAfter: number;
                    description: string;
                    createdAt: string;
                }>;
                total: number;
                page: number;
                size: number;
            }>>>('/api/points/transactions', { params: { page, size } });
            return data;
        },
    },

    // 공고별 관심(찜) — 관심치과와 무관, 채용공고 단위
    recruitmentFavorite: {
        add: async (recruitmentId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
                message: string;
            }>>>(`/api/favorites/recruitments/${recruitmentId}`);
            return data;
        },
        remove: async (recruitmentId: number) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
                message: string;
            }>>>(`/api/favorites/recruitments/${recruitmentId}`);
            return data;
        },
        check: async (recruitmentId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
            }>>>(`/api/favorites/recruitments/${recruitmentId}/check`);
            return data;
        },
    },
    favorite: {
        // 관심 치과 등록
        add: async (clinicId: number) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
                message: string;
            }>>>(`/api/favorites/clinics/${clinicId}`);
            return data;
        },
        // 관심 치과 삭제
        remove: async (clinicId: number) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
                message: string;
            }>>>(`/api/favorites/clinics/${clinicId}`);
            return data;
        },
        // 관심 치과 여부 확인
        check: async (clinicId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                isFavorite: boolean;
            }>>>(`/api/favorites/clinics/${clinicId}/check`);
            return data;
        },
        // 관심 치과 목록 조회
        getList: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Array<{
                id: number;
                name: string;
                address: string;
                city: string;
                district: string;
                pcThumbnail: string | null;
                mobileThumbnail: string | null;
                addedAt: string;
            }>>>>(`/api/favorites/clinics`);
            return data;
        },
        // 관심 치과 채용공고 조회
        getRecruitments: async (size: number = 10, offset: number = 0) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{
                list: any[];
                total: number;
            }>>>(`/api/favorites/clinics/recruitments`, { params: { size, offset } });
            return data;
        },
    },

    category: {
        list: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.CATEGORY.LIST);
            return data;
        },
    },

    // 신고/차단 (App Store Guideline 1.2 대응)
    moderation: {
        // 콘텐츠/사용자 신고. targetType: POST/POST_COMMENT/POST_REPLY/EXPERT_CONTENT/EXPERT_COMMENT/EXPERT_REPLY/SEMINAR_REVIEW/USER/THREAD…
        // 콘텐츠 신고의 작성자는 서버가 대상 id 로 찾는다 — targetUserNo 는 대화방 신고처럼 회원을 직접 가리키는 경우에만
        report: async (payload: {
            targetType: string;
            targetId: string;
            targetUserNo?: string;
            parentId?: string;
            reason: string;
            detail?: string;
        }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<null>>>(URLS.MODERATION.REPORT, payload);
            return data;
        },
        // 콘텐츠 작성자 차단 — 글·댓글·답글·후기 id 를 보내면 서버가 작성자를 찾아 차단한다. 그 사람의 글·댓글이 내 목록·상세에서 숨겨진다.
        blockByTarget: async (payload: { targetType: string; targetId: string; parentId?: string }) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<null>>>(URLS.MODERATION.BLOCK_BY_TARGET, payload);
            return data;
        },
        // 차단 해제 — 차단 목록(myBlocks)의 id 로
        unblock: async (blockId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<null>>>(URLS.MODERATION.UNBLOCK_BY_ID(blockId));
            return data;
        },
        // 내가 차단한 목록 — 차단 한 건의 id + 닉네임 스냅샷(상대 회원 번호는 내려오지 않는다)
        myBlocks: async () => {
            const { data } = await axios.get<
                any,
                AxiosResponse<
                    ValidateResponse<{
                        blockedUsers: { id: string; nickname: string | null; createdAt?: string }[];
                    }>
                >
            >(URLS.MODERATION.MY_BLOCKS);
            return data;
        },
    },

    expertContent: {
        getMeta: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.META);
            return data;
        },
        // 전문가 전용 유형(칼럼/뉴스/자료) 작성 가능 여부 조회. result.isActiveExpert
        // 비로그인 방문자의 401이 로그인 페이지로 튕기지 않도록 skipAuthHandler 지정(조용히 실패 → 기본 false)
        getWritePermission: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{ isActiveExpert: boolean }>>>(
                URLS.EXPERT_CONTENT.WRITE_PERMISSION,
                { skipAuthHandler: true },
            );
            return data;
        },
        list: async (params: {
            offset?: number;
            limit?: number;
            contentType?: string;
            categories?: string[];
            tags?: string[];
            search?: string;
            sort?: 'latest' | 'views' | 'likes' | 'comments';
        }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.LIST, {
                params,
                paramsSerializer: (p) =>
                    Object.entries(p)
                        .filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
                        .map(([k, v]) =>
                            Array.isArray(v)
                                ? v.map((vv) => `${k}=${encodeURIComponent(vv)}`).join('&')
                                : `${k}=${encodeURIComponent(v as string | number)}`
                        )
                        .join('&'),
            });
            return data;
        },
        hot: async (limit = 5, daysAgo = 7) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.HOT, {
                params: { limit, daysAgo },
            });
            return data;
        },
        my: async (offset = 0, limit = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.MY, {
                params: { offset, limit },
            });
            return data;
        },
        authoredBy: async (userNo: string, contentType?: string, offset = 0, limit = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.EXPERT_CONTENT.AUTHORED_BY(userNo),
                { params: { offset, limit, ...(contentType ? { contentType } : {}) } },
            );
            return data;
        },
        detail: async (id: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.DETAIL(id));
            return data;
        },
        fullComments: async (id: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.FULL_COMMENTS(id));
            return data;
        },
        create: async (payload: any) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.CREATE, payload);
            return data;
        },
        update: async (id: string, payload: any) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.UPDATE(id), payload);
            return data;
        },
        remove: async (id: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.DELETE(id));
            return data;
        },
        addComment: async (id: string, content: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.ADD_COMMENT(id), { content });
            return data;
        },
        updateComment: async (id: string, commentId: string, content: string) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.UPDATE_COMMENT(id, commentId), { content });
            return data;
        },
        deleteComment: async (id: string, commentId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.DELETE_COMMENT(id, commentId));
            return data;
        },
        addReply: async (
            id: string,
            commentId: string,
            content: string,
            targetReplyId?: string | null,
            targetUserNickname?: string | null,
        ) => {
            // 다른 답글에 다는 답글이면 그 답글 id — 대상 회원은 서버가 찾는다(화면은 회원 번호를 모른다)
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.EXPERT_CONTENT.ADD_REPLY(id, commentId),
                { content, targetReplyId: targetReplyId ?? null, targetUserNickname: targetUserNickname ?? null },
            );
            return data;
        },
        updateReply: async (id: string, commentId: string, replyId: string, content: string) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.UPDATE_REPLY(id, commentId, replyId), { content });
            return data;
        },
        deleteReply: async (id: string, commentId: string, replyId: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.DELETE_REPLY(id, commentId, replyId));
            return data;
        },
        incrementView: async (id: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.INCREMENT_VIEW(id));
            return data;
        },
        incrementDownload: async (id: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(URLS.EXPERT_CONTENT.INCREMENT_DOWNLOAD(id));
            return data;
        },
        toggleLike: async (id: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<{ liked: boolean; likeCount: number }>>>(
                URLS.EXPERT_CONTENT.TOGGLE_LIKE(id),
            );
            return data;
        },
        // 댓글·답글 좋아요 토글 — replyId 가 있으면 답글
        toggleCommentLike: async (id: string, commentId: string, replyId?: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<{ liked: boolean; likeCount: number }>>>(
                replyId
                    ? URLS.EXPERT_CONTENT.TOGGLE_REPLY_LIKE(id, commentId, replyId)
                    : URLS.EXPERT_CONTENT.TOGGLE_COMMENT_LIKE(id, commentId),
            );
            return data;
        },
    },

    // AI 질문·답변. 질문을 보내고 답을 받는 호출(SSE)은 aiChatStream.ts 의 streamAiChatMessage 를 쓴다.
    aiChat: {
        // 기능이 켜졌는지 — 로그인 쿠키를 함께 보내 그 회원 기준(전체 공개 또는 화이트리스트)으로 받는다.
        // 공개 API 라 비로그인 홈에서도 부른다(401 로 로그인 화면에 튕기지 않게 skipAuthHandler)
        getStatus: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatStatus>>>(
                URLS.AI_CHAT.STATUS,
                { skipAuthHandler: true },
            );
            return data;
        },
        // 쓸 수 있는 회원인지·동의했는지·오늘 남은 질문 수
        // silent: 다른 화면(속닥속닥 글쓰기)에서 조용히 물어보는 호출 — 401 이어도 로그인 화면으로 보내지 않는다
        getAccess: async (options?: { silent?: boolean }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatAccess>>>(
                URLS.AI_CHAT.ACCESS,
                options?.silent ? { skipAuthHandler: true } : undefined,
            );
            return data;
        },
        // 첫 사용 동의
        consent: async () => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ consented: boolean }>>>(URLS.AI_CHAT.CONSENT);
            return data;
        },
        // 빈 대화에 보여 줄 예시 질문(0~3개) — 실패하면 화면이 고정 문구를 쓴다
        // silent: 홈 말풍선처럼 조용히 물어보는 호출 — 401 이어도 로그인 화면으로 보내지 않는다
        getSuggestions: async (options?: { silent?: boolean }) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatSuggestions>>>(
                URLS.AI_CHAT.SUGGESTIONS,
                options?.silent ? { skipAuthHandler: true } : undefined,
            );
            return data;
        },
        // 홈 말풍선 개인화(원장·위생사 공간) — 조용히 부른다(401 이어도 로그인 화면으로 보내지 않는다)
        getHooks: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatHooks>>>(URLS.AI_CHAT.HOOKS, { skipAuthHandler: true });
            return data;
        },
        // 새 대화 — 첫 질문을 보낼 때 만든다
        create: async () => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ id: string }>>>(URLS.AI_CHAT.ROOT);
            return data;
        },
        // 내 질문 목록(메시지 없는 대화 제외, 최근 순)
        list: async (offset = 0, limit = 20) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatListResult>>>(URLS.AI_CHAT.ROOT, {
                params: { offset, limit },
            });
            return data;
        },
        detail: async (id: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiChatDetail>>>(URLS.AI_CHAT.DETAIL(id));
            return data;
        },
        remove: async (id: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(URLS.AI_CHAT.DETAIL(id));
            return data;
        },
        // 답변 평가 — 화면에서는 답변마다 한 번만 보낸다(평가하면 버튼이 사라진다). BAD 면 reason 필수
        saveFeedback: async (id: string, messageId: string, payload: AiChatFeedbackPayload) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.AI_CHAT.FEEDBACK(id, messageId),
                payload,
            );
            return data;
        },
        // 대화를 전문가 라운지 질문 글 초안으로 정리
        loungeDraft: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiLoungeDraft>>>(URLS.AI_CHAT.LOUNGE_DRAFT(id));
            return data;
        },
        // 대화를 속닥속닥 익명 글 초안으로 정리(질문권을 쓰지 않는다)
        communityDraft: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiCommunityDraft>>>(URLS.AI_CHAT.COMMUNITY_DRAFT(id));
            return data;
        },
        // [이대로 공고 등록하기] — 답의 공고 미리보기를 등록 초안으로(치과 계정만, 24시간)
        recruitmentDraft: async (id: string, messageId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiRecruitmentDraftCreated>>>(URLS.AI_CHAT.RECRUITMENT_DRAFT(id, messageId));
            return data;
        },
        // [이 내용으로 문의하기] — 문의 화면에 채울 초안(본인 대화만, 주문번호는 서버가 그때 채운다)
        inquiryDraft: async (id: string, messageId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<{ category: string; title: string; contents: string }>>>(URLS.AI_CHAT.INQUIRY_DRAFT(id, messageId));
            return data;
        },
        // 등록 화면이 읽는 공고 초안(만든 회원만·만료 전만)
        // 원장 질문 답 — 보기 하나
        answerOwnerAsk: async (askId: string, option: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<{ thanks: string }>>>(URLS.AI_CHAT.OWNER_ASK_ANSWER(askId), { option });
            return data;
        },
        // 대화로 공고 등록하기 — 상세 미리보기 값·등록(회원이 누를 때만)·등록 화면 초안
        recruitmentFlow: async (flowId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiRecruitmentFlowView>>>(URLS.AI_CHAT.RECRUITMENT_FLOW(flowId));
            return data;
        },
        recruitmentFlowDetailPreview: async (flowId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<Recruitment>>>(URLS.AI_CHAT.RECRUITMENT_FLOW_DETAIL_PREVIEW(flowId));
            return data;
        },
        publishRecruitmentFlow: async (flowId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiRecruitmentFlowPublished>>>(URLS.AI_CHAT.RECRUITMENT_FLOW_PUBLISH(flowId));
            return data;
        },
        // 빠진 칸 카드 [다 채웠어요] — 칸 이름 → 값들
        recruitmentFlowFill: async (flowId: string, values: Record<string, string[]>) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiRecruitmentFlowFillResult>>>(URLS.AI_CHAT.RECRUITMENT_FLOW_FILL(flowId), { values });
            return data;
        },
        // 지난 공고 다시 올리기 — 고르기 카드(PICK)·[새 공고로 다시 올리기](NEW). 질문 수를 쓰지 않는다
        repost: async (chatId: string, messageId: string, recruitmentId: number, action: 'PICK' | 'NEW') => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiRepostResult>>>(URLS.AI_CHAT.REPOST(chatId), { messageId, recruitmentId, action });
            return data;
        },
        recruitmentFlowEditorDraft: async (flowId: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiRecruitmentDraftCreated>>>(URLS.AI_CHAT.RECRUITMENT_FLOW_EDITOR(flowId));
            return data;
        },
        recruitmentDraftDetail: async (draftId: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiRecruitmentDraft>>>(URLS.AI_CHAT.RECRUITMENT_DRAFT_DETAIL(draftId));
            return data;
        },
        // 초안으로 등록한 라운지 글을 대화에 연결
        linkLounge: async (id: string, contentId: string) => {
            const { data } = await axios.patch<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.AI_CHAT.LOUNGE_LINK(id),
                { contentId },
            );
            return data;
        },
        // 공유 전 개인정보 검사(질문 수를 쓰지 않는다) — 찾은 것이 없으면 빈 배열
        checkShare: async (id: string) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<AiShareCheckResult>>>(URLS.AI_CHAT.SHARE_CHECK(id));
            return data;
        },
        // 공유 만들기·갱신 — 지금 시점의 사본을 만들고 링크를 돌려준다
        share: async (id: string, payload: AiSharePayload) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<AiShareLink>>>(URLS.AI_CHAT.SHARE(id), payload);
            return data;
        },
        // 공유 중지
        stopShare: async (id: string) => {
            const { data } = await axios.delete<any, AxiosResponse<ValidateResponse<any>>>(URLS.AI_CHAT.SHARE(id));
            return data;
        },
        // 공유받은 대화 보기 — 비로그인도 본다(401 로 로그인 화면에 튕기지 않게 skipAuthHandler). 중지된 공유는 410
        getShared: async (token: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<AiShareView>>>(URLS.AI_CHAT.SHARED(token), { skipAuthHandler: true });
            return data;
        },
        // 공유받은 대화 신고 — 비로그인도 한다
        reportShared: async (token: string, reason: AiShareReportReason) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<any>>>(
                URLS.AI_CHAT.SHARED_REPORTS(token),
                { reason },
                { skipAuthHandler: true },
            );
            return data;
        },
    },

    // 채용 공고 등록 후 1분 설문 + 홈 추천 24시간 — 치과 회원 전용(docs/recruitment-survey-boost-plan.md)
    recruitmentSurvey: {
        // 이 공고·이 계정에 안내를 띄울지 + 미리보기 카드
        getPrompt: async (recruitmentId: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<RecruitmentSurveyPrompt>>>(
                URLS.RECRUITMENT_SURVEY.PROMPT(recruitmentId),
            );
            return data;
        },
        // 제출 → 홈 추천 24시간. 같은 분기 두 번째는 409(메시지를 그대로 보여 준다)
        submit: async (payload: RecruitmentSurveyPayload) => {
            const { data } = await axios.post<any, AxiosResponse<ValidateResponse<RecruitmentSurveySubmitResult>>>(
                URLS.RECRUITMENT_SURVEY.SUBMIT,
                payload,
            );
            return data;
        },
        // [다시 보지 않기] — 계정 기준 서버 저장
        dismiss: async () => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<null>>>(URLS.RECRUITMENT_SURVEY.DISMISSAL);
            return data;
        },
    },
    salarySurvey: {
        // 현재 회차·참여 수·티저·내 응답. 공개 API 라 비로그인도 호출 가능 —
        // 401 인터셉터가 랜딩을 로그인 화면으로 튕기지 않도록 skipAuthHandler 를 준다.
        getCurrent: async () => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SalarySurveyCurrentResponse>>>(
                URLS.SALARY_SURVEY.CURRENT,
                { skipAuthHandler: true },
            );
            return data;
        },
        // 응답 저장(회차당 1건 upsert). 기응답자의 "변동 없음 확인"도 같은 호출이다.
        // 저장은 지금 열려 있는 회차에만 가능하므로 회차 키 자리에 `current` 별칭을 쓴다.
        saveResponse: async (payload: SalarySurveyResponsePayload) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<SalarySurveySaveResult>>>(
                URLS.SALARY_SURVEY.MY_RESPONSE(CURRENT_CAMPAIGN_KEY),
                payload,
            );
            return data;
        },
        // 내 급여 위치. 자격 없음(치과위생사 아님·면허 미인증)은 200 + result{eligible:false, reason} — isSalaryUnavailable 로 가른다.
        // 미발행/그룹부족(404)은 에러 응답 본문의 result.reason 으로 화면을 분기한다.
        getMyResult: async (campaignKey?: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SalarySurveyMyResult | SalarySurveyMyResultUnavailable>>>(
                URLS.SALARY_SURVEY.MY_RESULT(campaignKey ?? LATEST_KEY),
            );
            return data;
        },
        // 작년 조사 기준 내 위치. 자격 없음은 200 + result{eligible:false, reason}, 응답 없음/기준선 없음은 404 본문의 result.reason.
        getMyBaseline: async (year?: number) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SalaryBaselineComparison | SalaryBaselineUnavailable>>>(
                URLS.SALARY_SURVEY.MY_BASELINE(year === undefined ? LATEST_KEY : String(year)),
            );
            return data;
        },
        // 공개 리포트. 발행 전이면 404 — 비로그인도 열람 가능하므로 skipAuthHandler.
        getReport: async (campaignKey?: string) => {
            const { data } = await axios.get<any, AxiosResponse<ValidateResponse<SalarySurveyReport>>>(
                URLS.SALARY_SURVEY.REPORT(campaignKey ?? LATEST_KEY),
                { skipAuthHandler: true },
            );
            return data;
        },
        // 리포트 본 처리(멱등) — 결과/리포트를 보여 줄 때 부른다. 실패해도 화면에는 영향이 없어 skipAuthHandler.
        markReportSeen: async (campaignKey: string) => {
            const { data } = await axios.put<any, AxiosResponse<ValidateResponse<string>>>(
                URLS.SALARY_SURVEY.REPORT_VIEW(campaignKey),
                null,
                { skipAuthHandler: true },
            );
            return data;
        },
    },
};
